import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApplicationDecisionInput,
  ApplicationListQuery,
  SubmitApplicationInput,
} from '@teentack/shared';
import { jobKeys } from '@/features/jobs/use-jobs';
import {
  decideApplication,
  fetchJobApplications,
  fetchMyApplications,
  submitApplication,
  withdrawApplication,
} from './applications.api';

export const applicationKeys = {
  all: ['applications'] as const,
  mine: (query: Partial<ApplicationListQuery>) => ['applications', 'mine', query] as const,
  forJob: (query: Partial<ApplicationListQuery>) => ['applications', 'job', query] as const,
};

export function useMyApplications(query: Partial<ApplicationListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: applicationKeys.mine(query),
    queryFn: () => fetchMyApplications(query),
    enabled,
  });
}

export function useJobApplications(query: Partial<ApplicationListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: applicationKeys.forJob(query),
    queryFn: () => fetchJobApplications(query),
    enabled,
  });
}

export function useSubmitApplication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SubmitApplicationInput) => submitApplication(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: applicationKeys.all });
      void queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

export function useDecideApplication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ApplicationDecisionInput & { applicationId: string }) =>
      decideApplication(input.applicationId, { decision: input.decision }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: applicationKeys.all });
    },
  });
}

export function useWithdrawApplication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (applicationId: string) => withdrawApplication(applicationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: applicationKeys.all });
      void queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}
